import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  AfterViewInit,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterModule } from '@angular/router';
import { FGBSharedModule } from '@fgb/portal-component-library/src/lib/shared';
import { filter } from 'rxjs';
import gsap from 'gsap';

@Component({
  selector: 'fgb-route-nav',
  standalone: true,
  imports: [CommonModule, RouterModule, FGBSharedModule],
  templateUrl: './route-nav.component.html',
  styleUrl: './route-nav.component.scss',
})
export class RouteNavComponent implements OnInit, AfterContentInit, AfterViewInit {
  resizeTimeout: any;
  padding: number = 0;
  private router = inject(Router);
  @Input() indicatorStyle: 'rounded' | 'default' = 'default';
  @ContentChildren(RouterLink, { read: ElementRef }) navItems!: QueryList<ElementRef>;
  @ViewChild('tabsContainer', { static: true }) tabsContainerRef!: ElementRef;
  @ViewChild('indicator', { static: true }) indicatorRef!: ElementRef;
  @Output() onTabChange = new EventEmitter<string>();
  @HostListener('window:resize')
  onResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() =>
      this._init((width: number, x: number) => {
        gsap.set(this.indicatorRef.nativeElement, {
          x: x + this.padding / 2,
          width: width - this.padding,
        });
      })
    );
  }
  hasActive: boolean = true;

  ngOnInit() {
    if (this.indicatorStyle === 'default') {
      this.padding = 20;
    }
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.animateTab();
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this._init((width: number, x: number) => {
        gsap.set(this.indicatorRef.nativeElement, {
          x: x + this.padding / 2,
          width: width - this.padding,
        });
      });
    }, 100);
  }

  ngAfterContentInit(): void {}

  private _init(animateFn: (width: number, x: number) => void): void {
    requestAnimationFrame(() => {
      const container = this.tabsContainerRef.nativeElement;

      const tab = this.navItems.toArray().find((item) => item.nativeElement.classList.contains('active'));
      this.hasActive = !!tab;
      if (!container || !tab) return;

      const finalX = tab.nativeElement.offsetLeft - container.scrollLeft;

      const containerWidth = container.offsetWidth;
      const tabWidth = tab.nativeElement.offsetWidth;
      const tabLeft = tab.nativeElement.offsetLeft;
      const scrollLeft = tabLeft - containerWidth / 2 + tabWidth / 2;

      gsap.set(container, {
        scrollLeft: scrollLeft,
      });

      animateFn(tab.nativeElement.offsetWidth, finalX);
      const text = tab.nativeElement.querySelector('label')?.innerText;
      this.onTabChange.emit(text);
    });
  }

  animateTab(): void {
    requestAnimationFrame(() => {
      const containerRect = this.tabsContainerRef.nativeElement;
      const tab = this.navItems.toArray().find((item) => item.nativeElement.classList.contains('active'));

      this.hasActive = !!tab;
      const tabRect = tab?.nativeElement;
      if (!containerRect || !tabRect) return;
      const indicator = this.indicatorRef.nativeElement;
      const finalX = tabRect.getBoundingClientRect().left - containerRect.getBoundingClientRect().left + containerRect.scrollLeft;
      const tl = gsap.timeline();

      const containerWidth = containerRect.offsetWidth;
      const tabWidth = tabRect.offsetWidth;
      const tabLeft = tabRect.offsetLeft;
      const scrollLeft = tabLeft - containerWidth / 2 + tabWidth / 2;

      // Animate the indicator
      tl.to(indicator, {
        duration: 0.5,
        x: finalX + this.padding / 2,
        ease: 'back.out(1.2)',
        onStart: () => {
          gsap.to(indicator, {
            duration: 0.2,
            width: tabRect.getBoundingClientRect().width - this.padding,
            ease: 'power2.out',
          });
        },
      });

      tl.to(containerRect, {
        scrollLeft: scrollLeft,
      });

      const text = tab.nativeElement.querySelector('label').innerText;
      this.onTabChange.emit(text);
    });
  }
}
